// src/config/environment.js
const isDevelopment = process.env.NODE_ENV === 'development';
const isProduction = process.env.NODE_ENV === 'production';

const getApiUrl = () => {
  // PRIORITY 1: Environment variable (from .env file)
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL;
  }
  
  // PRIORITY 2: Production server detection
  if (window.location.hostname === 'ems.marindrive.in' || 
      window.location.hostname.includes('marindrive.in')) {
    return 'https://ems.marindrive.in/api';
  }
  
  // PRIORITY 3: Default local development
  return 'https://localhost:7014/api';
};

const getHubUrl = () => {
  // PRIORITY 1: Environment variable (from .env file)
  if (process.env.REACT_APP_HUB_URL) {
    return process.env.REACT_APP_HUB_URL;
  }
  
  // PRIORITY 2: Production server detection
  if (window.location.hostname === 'ems.marindrive.in' || 
      window.location.hostname.includes('marindrive.in')) {
    return 'https://ems.marindrive.in/electionHub';
  }
  
  // PRIORITY 3: Default local development
  return 'https://localhost:7014/electionHub';
};

// Get base URL without /api suffix (for image URLs)
const getBaseUrl = () => {
  const apiUrl = getApiUrl();
  return apiUrl.replace('/api', '');
};

export const config = {
  API_URL: getApiUrl(),
  HUB_URL: getHubUrl(),
  BASE_URL: getBaseUrl(), // For constructing image URLs
  ENCRYPTION_KEY: process.env.REACT_APP_ENCRYPTION_KEY || 'BiharElection2025SecretKey!@#',
  SESSION_TIMEOUT: parseInt(process.env.REACT_APP_SESSION_TIMEOUT || '900000'),
  isDevelopment,
  isProduction,
  DEBUG: isDevelopment,
  API_TIMEOUT: 30000,
  MAX_RETRIES: 3,
  ENABLE_SIGNALR: true,
  FALLBACK_POLL_INTERVAL: 5000
};

// Enhanced logging in development
if (isDevelopment) {
  console.log('========================================');
  console.log('🔧 Environment Configuration');
  console.log('========================================');
  console.log('API_URL:', config.API_URL);
  console.log('BASE_URL:', config.BASE_URL);
  console.log('HUB_URL:', config.HUB_URL);
  console.log('Environment:', process.env.NODE_ENV);
  console.log('Hostname:', window.location.hostname);
  console.log('Using .env?:', !!process.env.REACT_APP_API_URL);
  console.log('========================================');
  
  // Check if backend is accessible
  console.log('🔍 Checking backend connection...');
  
  const healthCheckUrl = `${config.BASE_URL}/api/health`;
  
  fetch(healthCheckUrl, {
    method: 'GET',
    headers: {
      'Accept': 'application/json'
    }
  })
    .then(response => {
      if (response.ok) {
        console.log('✅ Backend server is accessible');
        return response.json();
      } else {
        console.warn('⚠️ Backend server returned error:', response.status);
        throw new Error(`Backend returned status ${response.status}`);
      }
    })
    .then(data => {
      console.log('✅ Backend health check:', data);
      console.log('========================================');
    })
    .catch(error => {
      console.error('========================================');
      console.error('❌ Backend Connection Failed');
      console.error('========================================');
      console.error('Backend URL:', config.BASE_URL);
      console.error('Health Check URL:', healthCheckUrl);
      console.error('Error:', error.message);
      console.error('');
      console.error('💡 Troubleshooting Steps:');
      console.error('  1. Make sure your .NET API is running');
      console.error('     Command: dotnet run (in backend folder)');
      console.error('  2. Backend should be running on: https://localhost:7014');
      console.error('  3. Check if the URL in .env is correct');
      console.error('  4. Verify CORS is configured on backend');
      console.error('  5. Check if SSL certificate is valid');
      console.error('     Command: dotnet dev-certs https --trust');
      console.error('  6. Try accessing in browser: ' + healthCheckUrl);
      console.error('========================================');
    });
}

/**
 * Get full image URL from relative path
 * @param {string} imageUrl - Relative or absolute image URL
 * @returns {string} - Full image URL
 */
export const getFullImageUrl = (imageUrl) => {
  if (!imageUrl) {
    return 'https://via.placeholder.com/1920x600?text=No+Image';
  }
  
  // Already a full URL
  if (imageUrl.startsWith('http://') || imageUrl.startsWith('https://')) {
    return imageUrl;
  }
  
  // Relative URL - construct full path
  const baseUrl = config.BASE_URL;
  
  // Ensure single leading slash
  const cleanPath = imageUrl.startsWith('/') ? imageUrl : `/${imageUrl}`;
  
  const fullUrl = `${baseUrl}${cleanPath}`;
  
  // if (isDevelopment) {
  //   console.log('🖼️ Image URL:', {
  //     input: imageUrl,
  //     output: fullUrl
  //   });
  // }
  
  return fullUrl;
};

/**
 * Handle image load error with fallback
 * @param {Event} e - Error event
 * @param {string} fallbackUrl - Optional custom fallback URL
 */
export const handleImageError = (e, fallbackUrl = null) => {
  console.error('❌ Image failed to load:', e.target.src);
  
  const defaultFallback = 'https://via.placeholder.com/1920x600?text=Image+Not+Found';
  e.target.src = fallbackUrl || defaultFallback;
  
  // Prevent infinite loop
  e.target.onerror = null;
};

export default config;
